import { message, Spin } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { normalizeDocumentCategories } from 'features/files/utils/normalizeDocumentCategories'
import * as React from 'react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryPatchJson, RolodexEntryTagsType } from 'types'

import { DocumentAiFieldExtractor } from './components/DocumentAiFieldExtractor'
import { DocumentCategorySelector } from './components/DocumentCategorySelector'
import { DocumentList } from './components/DocumentList'
import { DocumentPreview } from './components/DocumentPreview'
import { DocumentTagging } from './components/DocumentTaggingForm'
import { useLinkedDocuments } from './hooks/useDocuments'
import { RolodexDocumentEntry, TaggedDocumentField } from './types'

export interface DocumentComponentProps {
  entry: GeckoEntry
  className?: string
  onComplete?: (documentIds: ReadonlyArray<string>) => void
  onError?: (error: string) => void
  defaultKeys?: string[]
  categories?: ReadonlyArray<string>
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.9375rem;
  width: 100%;
  align-items: flex-start;
`

const FileListSection = styled.div`
  flex: 0 0 auto;
  min-width: 220px;
  max-width: 320px;
`

const TagEditorSection = styled.div`
  flex: 1 1 0;
  min-width: 400px;
`

const PreviewSection = styled.div`
  flex: 2 1 0;
  min-width: 600px;
  min-height: 500px;
`

/**
 * Build initial document fields object with existing field data and any default fields (set to empty strings)
 */
const buildInitialDocumentFields = (
  documents: ReadonlyArray<RolodexDocumentEntry>
): Record<string, ReadonlyArray<TaggedDocumentField>> => {
  const fields: Record<string, ReadonlyArray<TaggedDocumentField>> = {}

  documents.forEach((doc) => {
    const existingFields: ReadonlyArray<TaggedDocumentField> = Object.entries(
      doc.fields
    ).map(([key, value]) => ({ key, value: String(value) }))

    fields[doc.entry_id] = existingFields
  })

  return fields
}

/**
 * Convert UI fields to API format
 */
const convertFieldsToApiFormat = (
  fields: ReadonlyArray<TaggedDocumentField>
): Record<string, string> => {
  const apiFields: Record<string, string> = {}
  fields
    .filter((field) => field.key.trim())
    .forEach((field) => {
      apiFields[field.key.trim()] = field.value.trim()
    })
  return apiFields
}

const removeEmptyFieldTags = (
  fields: ReadonlyArray<TaggedDocumentField>
): ReadonlyArray<TaggedDocumentField> => {
  return fields.filter(
    (field) => field.key.trim() !== '' && field.value.trim() !== ''
  )
}

/**
 * Merge two sets of fields, giving priority to the priority fields in the case of clashes
 * @param baseFields - The existing fields to merge.
 * @param priorityFields - The new fields to merge.
 * @returns The merged fields.
 */
const mergeFields = (
  baseFields: Record<string, string>,
  priorityFields: Record<string, string>
): Record<string, string> => {
  return { ...baseFields, ...priorityFields }
}

/**
 * A component which assists with tagging fields on documents.
 * @param entry - The entry, the documents of which will be tagged.
 * @param defaultKeys - The default keys to use for the tagging, to be initialised to empty strings if not present.
 */
export const DocumentTagger: React.FC<DocumentComponentProps> = ({
  entry,
  onComplete,
  onError,
  className,
  defaultKeys,
  categories,
}) => {
  const [selectedDocumentId, setSelectedDocumentId] = React.useState<
    string | null
  >(null)
  const [documentFields, setDocumentFields] = React.useState<
    Readonly<Record<string, ReadonlyArray<TaggedDocumentField>>>
  >({})
  const [isSavingFields, setIsSavingFields] = React.useState(false)
  const [isSavingCategory, setIsSavingCategory] = React.useState(false)

  const { rolodexPatch } = useRolodexEdit()
  const linkedDocumentTags = useMemo<RolodexEntryTagsType>(
    () => ({
      [entry.type]: entry.id,
    }),
    [entry.id, entry.type]
  )
  const {
    documents,
    isLoading: areDocumentsLoading,
    forceRefresh: forceRefreshDocuments,
  } = useLinkedDocuments(linkedDocumentTags)

  const selectedDocument = useMemo(
    () => documents.find((doc) => doc.entry_id === selectedDocumentId),
    [documents, selectedDocumentId]
  )

  const selectedDocumentName =
    selectedDocument?.filename || selectedDocument?.visible_name || 'document'

  React.useEffect(() => {
    if (documents.length === 0) return

    setSelectedDocumentId((prev) =>
      !prev || !documents.find((doc) => doc.entry_id === prev)
        ? documents[0].entry_id
        : prev
    )

    const newFields = buildInitialDocumentFields(documents)
    setDocumentFields({ ...newFields })
  }, [documents])

  const selectedDocumentFields = useMemo(
    () => documentFields[selectedDocumentId || ''] || [],
    [documentFields, selectedDocumentId]
  )

  const handleFieldsChange = useCallback(
    (fields: ReadonlyArray<TaggedDocumentField>) => {
      if (!selectedDocumentId) return
      setDocumentFields((prev) => ({
        ...prev,
        [selectedDocumentId]: fields,
      }))
    },
    [selectedDocumentId]
  )

  const handleSaveFields = useCallback(async () => {
    if (!selectedDocumentId || !selectedDocument) return
    setIsSavingFields(true)

    const fieldsToSubmit = convertFieldsToApiFormat(
      removeEmptyFieldTags(selectedDocumentFields)
    )

    if (Object.keys(fieldsToSubmit).length === 0) {
      message.warning('Please add at least one field with both key and value')
      return
    }

    try {
      const patchBody: RolodexEntryPatchJson = {
        id: selectedDocumentId,
        data: { fields: fieldsToSubmit },
      }

      await rolodexPatch([patchBody])
      message.success(`Fields saved for ${selectedDocumentName}`)

      await forceRefreshDocuments()

      onComplete?.([selectedDocumentId])
    } catch (error) {
      const errorMsg = `Failed to save fields for ${selectedDocumentName}: ${String(error)}`
      message.error(errorMsg)
      onError?.(errorMsg)
    } finally {
      setIsSavingFields(false)
    }
  }, [
    selectedDocumentId,
    selectedDocument,
    selectedDocumentName,
    selectedDocumentFields,
    rolodexPatch,
    forceRefreshDocuments,
    onComplete,
    onError,
  ])

  const handleSaveCategory = useCallback(
    async (category: string | null) => {
      if (!selectedDocumentId || !selectedDocument || !category) return
      setIsSavingCategory(true)

      try {
        const normalizedCategories = normalizeDocumentCategories([category])
        const patchBody: RolodexEntryPatchJson = {
          id: selectedDocumentId,
          data: {
            categories: normalizedCategories,
            category: normalizedCategories[0] ?? undefined,
          },
        }

        await rolodexPatch([patchBody])
        message.success(`Category saved for ${selectedDocumentName}`)

        await forceRefreshDocuments()

        onComplete?.([selectedDocumentId])
      } catch (error) {
        const errorMsg = `Failed to save category for ${selectedDocumentName}: ${String(error)}`
        message.error(errorMsg)
        onError?.(errorMsg)
      } finally {
        setIsSavingCategory(false)
      }
    },
    [
      selectedDocumentId,
      selectedDocument,
      selectedDocumentName,
      rolodexPatch,
      forceRefreshDocuments,
      onComplete,
      onError,
    ]
  )

  const handleApplyAiExtractedFields = useCallback(
    async (extractedFields: ReadonlyArray<TaggedDocumentField>) => {
      if (!selectedDocumentId || !selectedDocument) return
      setIsSavingFields(true)

      const existingFields = convertFieldsToApiFormat(
        removeEmptyFieldTags(selectedDocumentFields)
      )

      const aiExtractedFields = convertFieldsToApiFormat(
        removeEmptyFieldTags(extractedFields)
      )

      const fieldsPostMerge = mergeFields(existingFields, aiExtractedFields)

      try {
        const patchBody: RolodexEntryPatchJson = {
          id: selectedDocumentId,
          data: { fields: fieldsPostMerge },
        }

        await rolodexPatch([patchBody])
        message.success(`Fields saved for ${selectedDocumentName}`)

        await forceRefreshDocuments()

        onComplete?.([selectedDocumentId])
      } catch (error) {
        const errorMsg = `Failed to save fields for ${selectedDocumentName}: ${String(error)}`
        message.error(errorMsg)
        onError?.(errorMsg)
      } finally {
        setIsSavingFields(false)
      }
    },
    [
      selectedDocumentId,
      selectedDocument,
      selectedDocumentName,
      selectedDocumentFields,
      rolodexPatch,
      forceRefreshDocuments,
      onComplete,
      onError,
    ]
  )

  if (areDocumentsLoading) {
    return (
      <Container className={className}>
        <FileListSection>
          <Spin spinning />
        </FileListSection>
        <TagEditorSection />
        <PreviewSection />
      </Container>
    )
  }

  return (
    <Container className={className}>
      <FileListSection>
        <DocumentList
          documents={documents}
          selectedDocumentId={selectedDocumentId}
          onDocumentSelect={setSelectedDocumentId}
        />
      </FileListSection>
      {selectedDocument && (
        <PreviewSection>
          <DocumentPreview document={selectedDocument} height={800} />
        </PreviewSection>
      )}
      <TagEditorSection>
        {categories && categories.length > 0 && (
          <DocumentCategorySelector
            categories={categories}
            onSave={handleSaveCategory}
            saving={isSavingCategory}
            document={selectedDocument}
          />
        )}
        <DocumentAiFieldExtractor
          document={selectedDocument || undefined}
          onApply={handleApplyAiExtractedFields}
          defaultKeys={defaultKeys}
        />
        <DocumentTagging
          document={selectedDocument || null}
          fields={selectedDocumentFields}
          onFieldsChange={handleFieldsChange}
          onSave={handleSaveFields}
          saving={isSavingFields}
          defaultKeys={defaultKeys}
        />
      </TagEditorSection>
    </Container>
  )
}
